import { useEffect, useState } from "react";
import { MessageSquare, Save, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export const CHART_DESCRIPTION_FIELDS = [
  { key: "cash_flow", label: "Kasstroomtraject" },
  { key: "operational", label: "Operationeel profiel" },
  { key: "reserves_stack", label: "Reserves & voorzieningen — opbouw" },
  { key: "reserves", label: "Saldi van (bestemmings)reserves" },
];

// Eén dialoog voor alle grafiek-toelichtingen (vervangt de losse "Beschrijving"-knop per grafiek).
export default function ChartDescriptionsDialog({ open, onOpenChange, values = {}, onSave, maxLength = 500 }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setDraft(Object.fromEntries(CHART_DESCRIPTION_FIELDS.map((f) => [f.key, values[f.key] || ""]))); }, [open, values]);

  const save = async () => {
    setSaving(true);
    try {
      for (const f of CHART_DESCRIPTION_FIELDS) {
        const next = (draft[f.key] || "").trim();
        if (next !== (values[f.key] || "").trim()) await onSave(f.key, next);
      }
      toast.success("Toelichtingen opgeslagen");
      onOpenChange(false);
    } catch { toast.error("Opslaan mislukt"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="chart-descriptions-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2 pr-8"><MessageSquare className="w-5 h-5" /> Toelichtingen bij de grafieken</DialogTitle>
          <DialogDescription>Deze teksten verschijnen onder de betreffende grafiek op het scherm en in de PDF-export. Maximaal {maxLength} tekens per toelichting.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2 max-h-[60vh] overflow-y-auto pr-1">
          {CHART_DESCRIPTION_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="stat-label" htmlFor={`chart-desc-${f.key}`}>{f.label}</label>
              <textarea id={`chart-desc-${f.key}`} rows={3} maxLength={maxLength} value={draft[f.key] || ""} data-testid={`chart-desc-${f.key}-textarea`}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value.slice(0, maxLength) }))}
                placeholder="Bijv. “Uitgangspunt: inflatie 3%, geen nieuwe leningen na 2028”"
                className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y" />
              <div className="text-[10px] text-muted-foreground text-right font-mono-num">{(draft[f.key] || "").length} / {maxLength}</div>
            </div>
          ))}
        </div>
        <DialogFooter className="mt-2 gap-2">
          <button type="button" onClick={() => onOpenChange(false)} disabled={saving} data-testid="chart-descriptions-cancel" className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-1.5"><X className="w-4 h-4" /> Annuleren</button>
          <button type="button" onClick={save} disabled={saving} data-testid="chart-descriptions-save" className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-1.5"><Save className="w-4 h-4" /> {saving ? "Bezig…" : "Opslaan"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
